import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchPublicJobs } from '../../features/jobs/thunks/jobs.thunk';
import {
  EMPLOYMENT_TYPE_LABELS,
  JobEmploymentType,
  JobListing,
  JobLocationType,
  LOCATION_TYPE_LABELS,
} from '../../features/jobs/interface/jobs.interface';

const EMPLOYMENT_TYPES: JobEmploymentType[] = [
  'full-time',
  'part-time',
  'contract',
  'internship',
  'remote',
];

const LOCATION_TYPES: JobLocationType[] = ['hybrid', 'on-site', 'remote'];

type FiltersState = {
  employmentType: '' | JobEmploymentType;
  location: '';
  locationType: '' | JobLocationType;
};

const initialFilters: FiltersState = {
  employmentType: '',
  location: '',
  locationType: '',
};

const FilterSelect: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}> = ({ label, value, onChange, options }) => (
  <div className="flex-1 flex flex-col gap-2">
    <label className="text-[14px] font-medium font-title text-[#212121]">{label}</label>
    <div className="relative w-full h-10 border border-[#A7A7A7] rounded-[10px] flex items-center bg-white px-4">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-full appearance-none outline-none bg-transparent text-[14px] text-[#919191] font-description font-light cursor-pointer pr-5"
      >
        <option value="">All</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
        <svg width="14" height="14" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4.5 6.75L9 11.25l4.5-4.5" stroke="#919191" strokeWidth="1.13" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  </div>
);

const formatEmploymentLabel = (job: JobListing) => {
  const employment = EMPLOYMENT_TYPE_LABELS[job.employmentType];
  const work = LOCATION_TYPE_LABELS[job.locationType];
  return `${employment} - ${work}`;
};

const JobListings: React.FC = () => {
  const dispatch = useAppDispatch();
  const { jobs, isLoading, error } = useAppSelector((state) => state.jobs);

  const [filters, setFilters] = useState<FiltersState>(initialFilters);

  useEffect(() => {
    dispatch(
      fetchPublicJobs({
        page: 1,
        limit: 50,
        ...(filters.employmentType ? { employmentType: filters.employmentType } : {}),
        ...(filters.location ? { location: filters.location } : {}),
        ...(filters.locationType ? { locationType: filters.locationType } : {}),
      })
    );
  }, [dispatch, filters.employmentType, filters.location, filters.locationType]);

  const locationOptions = useMemo(() => {
    const unique = Array.from(new Set(jobs.map((j) => j.location).filter(Boolean)));
    return unique.map((loc) => ({ value: loc, label: loc }));
  }, [jobs]);

  return (
    <section className="bg-white py-12">
      <div className="layout-container max-w-[1080px] mx-auto px-5 flex flex-col items-center gap-10">
        <h2 className="text-[32px] md:text-[40px] font-medium text-center font-title text-[#212121]">
          Currently open position
        </h2>

        <div className="w-full flex flex-col gap-7">
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-3 w-full">
            <FilterSelect
              label="Employment Type"
              value={filters.employmentType}
              onChange={(v) => setFilters((p) => ({ ...p, employmentType: v as FiltersState['employmentType'] }))}
              options={EMPLOYMENT_TYPES.map((t) => ({ value: t, label: EMPLOYMENT_TYPE_LABELS[t] }))}
            />
            <FilterSelect
              label="Location"
              value={filters.location}
              onChange={(v) => setFilters((p) => ({ ...p, location: v as FiltersState['location'] }))}
              options={locationOptions}
            />
            <FilterSelect
              label="Work Type"
              value={filters.locationType}
              onChange={(v) => setFilters((p) => ({ ...p, locationType: v as FiltersState['locationType'] }))}
              options={LOCATION_TYPES.map((t) => ({ value: t, label: LOCATION_TYPE_LABELS[t] }))}
            />
          </div>

          {/* Job List */}
          <div className="flex flex-col w-full min-h-[180px]">
            {isLoading ? (
              <div className="py-12 flex flex-col items-center gap-2 text-[#919191] font-description text-[14px]">
                <div className="w-6 h-6 border-2 border-gray-200 border-t-[#212121] rounded-full animate-spin" />
                Loading jobs...
              </div>
            ) : error ? (
              <div className="py-12 flex flex-col items-center gap-2 text-center">
                <p className="text-red-600 font-description text-[14px]">{error}</p>
                <button
                  type="button"
                  onClick={() => dispatch(fetchPublicJobs({ page: 1, limit: 50 }))}
                  className="text-[14px] font-medium font-title text-[#212121] underline"
                >
                  Try again
                </button>
              </div>
            ) : jobs.length === 0 ? (
              <div className="py-12 flex flex-col items-center gap-2 text-center">
                <p className="text-[#212121] font-title text-[14px] font-medium">No open positions match your filters.</p>
                <p className="text-[#919191] font-description text-[14px]">Try adjusting your filters or check back soon.</p>
              </div>
            ) : (
              jobs.map((job) => (
                <div
                  key={job.id}
                  className="w-full flex flex-col md:flex-row bg-white border-b-[0.5px] border-[#E4E4E4] items-stretch md:items-center gap-6 group hover:bg-gray-50/30 transition-colors"
                >
                  {/* Location Column */}
                  <div className="w-full md:w-[170px] md:border-r-[0.5px] border-[#E4E4E4] px-4 py-5 flex flex-col justify-start">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center w-4 h-4">
                        <svg width="12" height="14" viewBox="0 0 24 24" fill="none" stroke="#212121" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                          <circle cx="12" cy="10" r="3"></circle>
                        </svg>
                      </div>
                      <span className="text-[14px] font-normal font-title text-[#919191]">
                        {job.location}
                      </span>
                    </div>
                  </div>

                  {/* Details Column */}
                  <div className="flex-1 py-5 pr-4 md:pr-0 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div className="flex flex-col gap-2 max-w-[460px]">
                      <h3 className="text-[16px] font-medium font-title text-[#212121] group-hover:text-primary transition-colors">
                        {job.title}
                      </h3>
                      <div className="flex items-center gap-2">
                        <svg width="14" height="14" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="1.5" y="1.5" width="15" height="15" rx="2" stroke="#171717" strokeWidth="1.12" />
                          <path d="M8.72 5.63v5.75l4 2.5" stroke="#171717" strokeWidth="1.12" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span className="text-[14px] font-normal font-title text-[#919191]">{formatEmploymentLabel(job)}</span>
                      </div>
                      <p className="text-[14px] font-light font-description text-[#919191] leading-[1.55] line-clamp-3">
                        {job.description}
                      </p>
                    </div>

                    <div className="mt-3 md:mt-0 flex-shrink-0">
                      <button className="h-9 px-5 bg-[#212121] text-white rounded-[8px] flex items-center justify-center gap-2 hover:bg-black transition-colors">
                        <span className="text-[14px] font-medium font-title">View Job Insights</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default JobListings;
